/**
 * APX WorkCenter — Reset Password Page
 * Validates the token from the URL and lets the user set a new password.
 * Design: "Gold Standard" — matches Login.tsx
 */

import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { trpcClient } from "@/lib/trpc";

const LOGIN_BG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663412810690/7y9xgY6crGLAQqoXCAMXSi/apx-login-bg-JwAEQh2ud7ykJp752TATLq.webp";

const APX_LOGO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310419663029793542/5GqF8Kn9QcwXve8h8refaY/apx-logo-final_808b0f18.png";

export default function ResetPassword() {
  const [, navigate] = useLocation();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  // NOTE: Using trpcClient directly (not useMutation hook) to avoid React Error #185.
  // useMutation creates a new options object on every render → infinite loop in production.

  useEffect(() => {
    document.title = "Reset Password — APX OPS HUB";
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) {
      setToken(t);
    } else {
      setStatus("error");
      setErrorMsg("No reset token found. Please request a new password reset link.");
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await trpcClient.staffManagement.confirmPasswordReset.mutate({ token, newPassword });
      setStatus("success");
    } catch (err: unknown) {
      setStatus("error");
      const message = err instanceof Error ? err.message : "Something went wrong. Please request a new reset link.";
      setErrorMsg(message.replace("TRPCClientError: ", ""));
    }
  };

  // Text colors for dark card background
  const CREAM = "oklch(0.95 0.01 85)";
  const SOFT_WHITE = "oklch(0.82 0.01 85)";
  const LABEL = "oklch(0.75 0.01 85)";

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center relative"
      style={{
        backgroundImage: `url(${LOGIN_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Brand green overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, oklch(0.22 0.06 185 / 0.90) 0%, oklch(0.18 0.05 185 / 0.85) 50%, oklch(0.20 0.06 185 / 0.88) 100%)",
        }}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-fade-slide-up">

        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-5">
            <img
              src={APX_LOGO}
              alt="APX Logo"
              className="h-24 w-auto"
              style={{ filter: "drop-shadow(0 6px 20px oklch(0.92 0.005 240 / 40%))" }}
            />
          </div>
          <h1
            className="text-4xl font-bold mb-2"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: CREAM,
              textShadow: "0 2px 12px oklch(0 0 0 / 40%)",
            }}
          >
            APX
          </h1>
          <p
            className="text-sm tracking-[0.25em] uppercase font-medium"
            style={{ color: SOFT_WHITE }}
          >
            APX OPS HUB
          </p>
        </div>

        {/* Reset Form Card */}
        <div
          className="rounded-2xl p-8"
          style={{
            background:
              "linear-gradient(160deg, oklch(0.29 0.06 185 / 0.92) 0%, oklch(0.25 0.06 185 / 0.95) 100%)",
            backdropFilter: "blur(24px)",
            border: "1px solid oklch(1 0 0 / 14%)",
            boxShadow:
              "0 32px 80px oklch(0 0 0 / 45%), 0 0 0 1px oklch(0.92 0.005 240 / 8%), inset 0 1px 0 oklch(1 0 0 / 10%)",
          }}
        >
          {/* Gold top accent line */}
          <div
            className="w-12 h-0.5 rounded-full mb-6"
            style={{
              background:
                "linear-gradient(90deg, oklch(0.72 0.14 75), oklch(0.72 0.14 75 / 0%))",
            }}
          />

          <h2
            className="text-xl font-semibold mb-1"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: CREAM,
            }}
          >
            Set New Password
          </h2>
          <p className="text-sm mb-7" style={{ color: SOFT_WHITE }}>
            Choose a new password for your APX Ops account.
          </p>

          {status === "success" ? (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: "oklch(0.72 0.14 75 / 15%)" }}
              >
                <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: "oklch(0.72 0.14 75)" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-base font-semibold" style={{ color: CREAM }}>Password updated!</p>
              <p className="text-sm" style={{ color: SOFT_WHITE }}>
                Your password has been changed. You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate("/login")}
                className="mt-2 w-full py-3 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200"
                style={{
                  background: "linear-gradient(135deg, oklch(0.82 0.14 75), oklch(0.72 0.14 75))",
                  color: "oklch(0.14 0.04 185)",
                  boxShadow: "0 4px 20px oklch(0.72 0.14 75 / 35%)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Go to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password */}
              <div>
                <label
                  className="block text-sm font-semibold uppercase tracking-wider mb-2"
                  style={{ color: LABEL }}
                >
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                  style={{
                    background: "oklch(0.33 0.05 185 / 0.8)",
                    border: "1px solid oklch(1 0 0 / 14%)",
                    color: CREAM,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "oklch(0.72 0.14 75 / 70%)";
                    e.target.style.boxShadow = "0 0 0 3px oklch(0.72 0.14 75 / 12%)";
                    e.target.style.background = "oklch(0.35 0.05 185 / 0.9)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "oklch(1 0 0 / 14%)";
                    e.target.style.boxShadow = "none";
                    e.target.style.background = "oklch(0.33 0.05 185 / 0.8)";
                  }}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  className="block text-sm font-semibold uppercase tracking-wider mb-2"
                  style={{ color: LABEL }}
                >
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
                  style={{
                    background: "oklch(0.33 0.05 185 / 0.8)",
                    border: "1px solid oklch(1 0 0 / 14%)",
                    color: CREAM,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "oklch(0.72 0.14 75 / 70%)";
                    e.target.style.boxShadow = "0 0 0 3px oklch(0.72 0.14 75 / 12%)";
                    e.target.style.background = "oklch(0.35 0.05 185 / 0.9)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "oklch(1 0 0 / 14%)";
                    e.target.style.boxShadow = "none";
                    e.target.style.background = "oklch(0.33 0.05 185 / 0.8)";
                  }}
                />
              </div>

              {/* Error */}
              {(status === "error" || errorMsg) && (
                <div
                  className="flex items-center gap-2.5 px-4 py-3 rounded-xl"
                  style={{
                    background: "oklch(0.55 0.18 25 / 15%)",
                    border: "1px solid oklch(0.55 0.18 25 / 35%)",
                  }}
                >
                  <svg
                    className="w-4 h-4 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    style={{ color: "oklch(0.72 0.18 25)" }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm" style={{ color: CREAM }}>
                    {errorMsg}
                  </span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === "loading" || !token}
                className="w-full py-3.5 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200 mt-1 disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(135deg, oklch(0.82 0.14 75), oklch(0.72 0.14 75))",
                  color: "oklch(0.14 0.04 185)",
                  boxShadow:
                    "0 4px 20px oklch(0.72 0.14 75 / 35%), inset 0 1px 0 oklch(1 0 0 / 20%)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {status === "loading" ? "Updating…" : "Update Password"}
              </button>

              <p className="text-center text-sm mt-2" style={{ color: SOFT_WHITE }}>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="underline"
                  style={{ color: "oklch(0.72 0.14 75 / 80%)", background: "none", border: "none", cursor: "pointer" }}
                >
                  Back to sign in
                </button>
              </p>
            </form>
          )}
        </div>

        <p
          className="text-center text-sm mt-5"
          style={{ color: SOFT_WHITE }}
        >
          APX · Secure Internal Platform · apxsystem.io
        </p>
      </div>
    </div>
  );
}
